import externalLinkIcon from "@tabler/icons/outline/external-link.svg";
import linkIcon from "@tabler/icons/outline/link.svg";
import playerPlayIcon from "@tabler/icons/outline/player-play.svg";
import React, { useState } from "react";

import Blurhash from "@/components/blurhash";
import { Icon, HStack, Stack, Text } from "@/components/ui";
import { type Card as CardEntity } from "@/schemas";
import { cn } from "@/utils/cn";
import { addAutoPlay } from "@/utils/media";
import { getAspectRatio } from "@/utils/media-aspect-ratio";

const MAX_TITLE = 120;
const MAX_DESCRIPTION = 200;

const trim = (text: string, len: number): string => {
  const cut = text.indexOf(" ", len);

  if (cut === -1) {
    return text;
  }

  return text.substring(0, cut) + (text.length > len ? "…" : "");
};

interface StatusCardProps {
  card: CardEntity;
  horizontal?: boolean;
  infoActionComponent?: React.JSX.Element;
  thumbnailPreview?: boolean;
}

const StatusCard = (props: StatusCardProps) => {
  const { card, infoActionComponent, thumbnailPreview = true } = props;

  const isRumble = card.provider_name === "Rumble.com";
  const isVideo = card.type === "video";
  const horizontal =
    typeof props.horizontal === "boolean" ? props.horizontal : isVideo;

  const [embedded, setEmbedded] = useState(isRumble);

  const wrapperClassName = cn(
    "rounded-2xl border border-solid border-gray-200 text-sm text-gray-800 no-underline dark:border-gray-800 dark:text-gray-200",
    { flex: !horizontal },
  );
  const containerClassName =
    "relative w-full flex-[0_0_40%] overflow-hidden [&_iframe]:size-full! rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none";

  if (isVideo && thumbnailPreview) {
    return (
      <div className={wrapperClassName}>
        {embedded ? (
          <div
            className={containerClassName}
            dangerouslySetInnerHTML={{
              __html: isRumble ? card.html : addAutoPlay(card.html),
            }}
            style={{
              width: "100%",
              aspectRatio: getAspectRatio(card.width, card.height),
            }}
          />
        ) : (
          <div className={containerClassName}>
            <Blurhash
              className="absolute inset-0 -z-10 size-full"
              hash={card.blurhash}
            />
            <Thumbnail card={card} />

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center justify-center rounded-full bg-gray-500/90 px-4 py-3 shadow-md dark:bg-gray-700/90">
                <HStack space={3} alignItems="center">
                  <button
                    onClick={() => setEmbedded(true)}
                    className="appearance-none text-gray-700 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-100"
                  >
                    <Icon
                      src={playerPlayIcon}
                      className="size-6 text-inherit"
                    />
                  </button>

                  {horizontal && (
                    <a
                      onClick={(e) => e.stopPropagation()}
                      href={card.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-100"
                    >
                      <Icon
                        src={externalLinkIcon}
                        className="size-6 text-inherit"
                      />
                    </a>
                  )}
                </HStack>
              </div>
            </div>
          </div>
        )}
        <Description card={card} infoActionComponent={infoActionComponent} />
      </div>
    );
  }

  return (
    <a
      href={card.url}
      className={cn(
        "flex flex-col overflow-hidden md:flex-row",
        "cursor-pointer hover:bg-gray-100 hover:no-underline dark:hover:bg-primary-800/30",
        wrapperClassName,
      )}
      target="_blank"
      rel="noopener noreferrer"
    >
      {card.image && thumbnailPreview && (
        <div
          className={cn(
            "md:size-auto md:shrink-0",
            {
              "h-auto": horizontal,
              "h-50": !horizontal,
            },
            containerClassName,
          )}
        >
          <Blurhash
            className="absolute inset-0 -z-10 size-full"
            hash={card.blurhash}
          />
          <Thumbnail card={card} />
        </div>
      )}
      <Description card={card} infoActionComponent={infoActionComponent} />
    </a>
  );
};

const Title = ({ card }: { card: CardEntity }) => {
  const trimmedTitle = trim(card.title, MAX_TITLE);
  const isLink = card.type === "link";

  if (!trimmedTitle) return null;

  if (!isLink)
    return (
      <Text>
        <a
          onClick={(e) => e.stopPropagation()}
          href={card.url}
          title={trimmedTitle}
          rel="noopener"
          target="_blank"
          data-testid="status-card-title"
        >
          {trimmedTitle}
        </a>
      </Text>
    );

  return (
    <Text>
      <span title={trimmedTitle}>{trimmedTitle}</span>
    </Text>
  );
};

const Description = ({
  card,
  infoActionComponent,
}: {
  card: CardEntity;
  infoActionComponent?: React.JSX.Element;
}) => {
  const trimmedTitle = trim(card.title, MAX_TITLE);
  const trimmedDescription = trim(card.description, MAX_DESCRIPTION);

  return (
    <HStack
      alignItems={trimmedTitle || trimmedDescription ? "start" : "center"}
      justifyContent="between"
      className="p-4"
    >
      <HStack space={2} alignItems="start">
        <Text tag="span" theme="muted">
          <Icon src={linkIcon} className="size-4.5" />
        </Text>

        <Stack space={1} className="flex-1 overflow-hidden">
          <Text tag="span" theme="muted" size="sm" weight="medium">
            {card.provider_name}
          </Text>

          <Title card={card} />

          {trimmedDescription && (
            <Text size="sm" theme="muted" data-testid="status-card-description">
              {trimmedDescription}
            </Text>
          )}
        </Stack>
      </HStack>

      <div className="relative">{infoActionComponent}</div>
    </HStack>
  );
};

const Thumbnail = ({ card }: { card: CardEntity }) => {
  return (
    <div
      style={{
        backgroundImage: `url(${card.image})`,
        width: "100%",
        aspectRatio: getAspectRatio(card.width, card.height),
      }}
      className="block size-full bg-cover bg-center object-cover"
      data-testid="status-card-image"
    />
  );
};

export default StatusCard;
